import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PageSize {
  name: string;
  width: number;
  height: number;
  unit: 'mm' | 'in';
}

export const PAGE_SIZES: PageSize[] = [
  { name: 'A5', width: 148, height: 210, unit: 'mm' },
  { name: 'A4', width: 210, height: 297, unit: 'mm' },
  { name: 'Letter', width: 8.5, height: 11, unit: 'in' },
  { name: '5×7"', width: 5, height: 7, unit: 'in' },
  { name: '4×6"', width: 4, height: 6, unit: 'in' },
];

interface PDFDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (pageSize: PageSize, quality: 'standard' | 'high', includeBleed: boolean, removeBackground: boolean, removeBorder: boolean) => void;
  eventName?: string;
}

const PDFDownloadModal: React.FC<PDFDownloadModalProps> = ({
  isOpen,
  onClose,
  onDownload,
  eventName,
}) => {
  const { t } = useTranslation();
  const [selectedSize, setSelectedSize] = useState<PageSize>(PAGE_SIZES[0]);
  const [quality, setQuality] = useState<'standard' | 'high'>('standard');
  const [includeBleed, setIncludeBleed] = useState(false);
  const [removeBackground, setRemoveBackground] = useState(false);
  const [removeBorder, setRemoveBorder] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!isOpen) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onDownload(selectedSize, quality, includeBleed, removeBackground, removeBorder);
      onClose();
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('pdf.modal.title')}
          </h2>
          <p className="text-gray-600">
            {t('pdf.modal.description')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Page Size Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('pdf.modal.pageSizeLabel')}
            </label>
            <div className="space-y-2">
              {PAGE_SIZES.map((size) => (
                <label
                  key={size.name}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedSize.name === size.name
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="pageSize"
                    value={size.name}
                    checked={selectedSize.name === size.name}
                    onChange={() => setSelectedSize(size)}
                    className="mr-3 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium">{size.name}</span>
                  <span className="ml-auto text-xs text-gray-500">
                    {size.width} × {size.height} {size.unit}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Quality Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('pdf.modal.qualityLabel')}
            </label>
            <div className="space-y-2">
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                quality === 'standard'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}>
                <input
                  type="radio"
                  name="quality"
                  value="standard"
                  checked={quality === 'standard'}
                  onChange={() => setQuality('standard')}
                  className="mr-3 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-sm font-medium">{t('pdf.modal.standardQuality')}</span>
                  <p className="text-xs text-gray-500">{t('pdf.modal.standardQualityDesc')}</p>
                </div>
              </label>
              <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                quality === 'high'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}>
                <input
                  type="radio"
                  name="quality"
                  value="high"
                  checked={quality === 'high'}
                  onChange={() => setQuality('high')}
                  className="mr-3 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <span className="text-sm font-medium">{t('pdf.modal.highQuality')}</span>
                  <p className="text-xs text-gray-500">{t('pdf.modal.highQualityDesc')}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Print Options */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">{t('pdf.modal.printOptions')}</h3>
            
            {/* Bleed Marks Option */}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeBleed}
                onChange={(e) => setIncludeBleed(e.target.checked)}
                className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('pdf.modal.includeBleed')}
                </span>
                <p className="text-xs text-gray-500">{t('pdf.modal.includeBleedDesc')}</p>
              </div>
            </label>

            {/* Remove Background Option */}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={removeBackground}
                onChange={(e) => setRemoveBackground(e.target.checked)}
                className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('pdf.modal.removeBackground')}
                </span>
                <p className="text-xs text-gray-500">{t('pdf.modal.removeBackgroundDesc')}</p>
              </div>
            </label>

            {/* Remove Border Option */}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={removeBorder}
                onChange={(e) => setRemoveBorder(e.target.checked)}
                className="mr-3 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('pdf.modal.removeBorder')}
                </span>
                <p className="text-xs text-gray-500">{t('pdf.modal.removeBorderDesc')}</p>
              </div>
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-8">
          <button
            type="button"
            onClick={onClose}
            disabled={isDownloading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {t('pdf.modal.cancelButton')}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className={`flex-1 px-4 py-2 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isDownloading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
            }`}
          >
            {isDownloading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('pdf.modal.downloading')}
              </span>
            ) : (
              t('pdf.modal.downloadButton')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFDownloadModal;